import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useState, Suspense, useCallback, useEffect } from "react";
import { Tab, Tabs, makeStyles, Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress, useTheme, useMediaQuery, Grid, LinearProgress, Box, Icon } from "@material-ui/core";
import clsx from "clsx";
import { useGetDebugSectionProps } from "@ploy-lib/calculation";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Section, customSections } from "../section";
import { ErrorDisplay } from "@ploy-lib/types";
import { useTemplateSectionIsVisible, useTemplateFieldIsVisible, useSectionHeader } from "../hooks";
import { FieldContainer, useFormState } from "..";
import { getIn, useFormikContext } from "formik";
import { getRegisteredSectionLayouts } from "../section/sectionLayoutDescriptions";
import { FormattedMessage } from "react-intl";
import { get } from "../utils";
const usePanelStyles = makeStyles(theme => ({
    root: {},
    sectionGroupsContainer: {
        width: "100%",
        minHeight: (props) => props.minHeight
    },
    tabsIndicator: {
        backgroundColor: "inherit"
    },
    tabsElevation: (props) => ({
        boxShadow: props.elevation ? theme.shadows[props.elevation] : "none"
    }),
    tabsRoundedTop: {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius
    },
    tabRoot: {
        fontWeight: 700,
        backgroundColor: theme.palette.common.white,
        "&:not($tabSelected)": {
            backgroundColor: theme.palette.grey[300]
        }
    },
    tabSelected: {}
}), { name: "DployFormPanel" });
const PanelImpl = (props) => {
    const { sections = [], initialTab, square, disableTabs, hideForMobile, ...restProps } = props;
    const theme = useTheme();
    const mobileMediaQuery = useMediaQuery(theme.breakpoints.down("xs"));
    const isMobile = props.desktopViewForMobile ? false : mobileMediaQuery;
    const isSectionVisible = useTemplateSectionIsVisible();
    const panelClasses = usePanelStyles(props);
    const visibleSections = sections
        .filter(isSectionVisible)
        .reduce((sectionsSoFar, section) => {
        // Handle merging of sections
        if (sectionsSoFar.length === 0 ||
            !section.mergeSectionWithPrevious ||
            Boolean(sectionsSoFar[sectionsSoFar.length - 1].layout)) {
            sectionsSoFar.push(section);
            return sectionsSoFar;
        }
        sectionsSoFar[sectionsSoFar.length - 1] = {
            ...sectionsSoFar[sectionsSoFar.length - 1],
            fields: [
                ...sectionsSoFar[sectionsSoFar.length - 1].fields,
                ...section.fields
            ]
        };
        return sectionsSoFar;
    }, [])
        .filter(isSectionVisible);
    const groupedSections = visibleSections.reduce((acc, s) => {
        let group;
        if (s.group) {
            group = acc.find(g => g[0].group === s.group);
        }
        else if (acc.length > 0 && !acc[acc.length - 1][0].group) {
            group = acc[acc.length - 1];
        }
        if (group)
            group.push(s);
        else
            acc.push([s]);
        return acc;
    }, []);
    const firstTab = visibleSections.find(s => !!s.tabGroup);
    const [tab, setTab] = useState(initialTab || (firstTab && firstTab.tabGroup));
    const visibleTabs = new Set(visibleSections.map(s => s.tabGroup).filter(s => !!s));
    const enableTabs = visibleTabs.size > 1 && !disableTabs;
    if (visibleSections.length <= 0 || (isMobile && hideForMobile))
        return null;
    return (_jsxs("div", { className: panelClasses.root, children: [_jsx(Grid, { container: true, className: clsx(panelClasses.sectionGroupsContainer), children: groupedSections
                    .map(g => (enableTabs ? g.filter(s => !s.tabGroup) : g))
                    .filter(g => g.length > 0)
                    .map((g, idx) => {
                    const [firstSection = null] = g;
                    return (_jsx(SectionGroup, { square: square, group: g, enableTabs: enableTabs, ...restProps }, idx + (firstSection?.group ?? "")));
                }) }), enableTabs && (_jsx("div", { className: clsx(panelClasses.sectionGroupsContainer), children: _jsx(Tabs, { variant: "fullWidth", value: tab, onChange: (_, value) => setTab(value), classes: {
                        indicator: panelClasses.tabsIndicator
                    }, className: clsx(panelClasses.tabsElevation, {
                        [panelClasses.tabsRoundedTop]: !square
                    }), children: [...visibleTabs].map(label => (_jsx(Tab, { label: label, classes: {
                            selected: panelClasses.tabSelected,
                            root: panelClasses.tabRoot
                        }, value: label }, label))) }) })), enableTabs && (_jsx(Grid, { container: true, className: clsx(panelClasses.sectionGroupsContainer), children: enableTabs &&
                    groupedSections
                        .map(g => g.filter(s => s.tabGroup === tab))
                        .filter(g => g.length > 0)
                        .map((g, idx) => (_jsx(SectionGroup, { group: g, enableTabs: enableTabs, ...restProps }, idx.toString()))) }))] }));
};
const useSectionGroupStyles = makeStyles(theme => ({
    root: {},
    expansionWrapper: {},
    expansionContent: {
        flexDirection: "column",
        justifyContent: "center"
    },
    expandedGroupContent: {},
    expansionLabel: {
        margin: "16px 0"
    },
    expansionLabelWithSubTitle: {
        marginBottom: 0
    },
    expansionSubTitle: {
        textAlign: "center"
    },
    expansionLabelUppercase: {
        textTransform: "uppercase"
    },
    expansionLabelBold: {
        fontWeight: "bold"
    },
    centerSectionTitle: {
        textAlign: "center"
    },
    marginLeft: {},
    expansionSummary: {
        paddingTop: 0,
        paddingBottom: 0,
        display: "flex",
        minHeight: theme.spacing(8),
        height: "auto"
    },
    expandIcon: {
        padding: 0,
        position: "absolute",
        right: 0,
        top: "50%",
        transform: "translateY(-50%) rotate(180deg)!important"
    },
    expandIconCollapsed: {
        position: "absolute",
        right: 0,
        top: "50%",
        transform: "translateY(-50%)!important"
    },
    expandIconInner: {},
    fullWidth: {
        maxWidth: "none",
        margin: 0
    },
    groupBorder: {
        border: "1px solid black",
        borderRadius: "4px"
    },
    groupIconContainer: {
        minWidth: 40
    },
    groupIcon: {
        textAlign: "center",
        overflow: "visible",
        width: "1.25em"
    },
    hasTabs: {
        "&:first-child": {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0
        }
    },
    //
    sectionGroup: (props) => ({
        maxWidth: props.maxWidth ? props.maxWidth : 550,
        margin: "auto"
    }),
    sectionGroupWrapper: {
        padding: theme.spacing(1),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(4)
    },
    statusIcon: {
        marginRight: 8
    },
    transparent: {
        backgroundColor: "inherit",
        border: "none"
    },
    groupBackgroundColor: (props) => ({
        backgroundColor: theme.palette?.[props.group[0].groupBackgroundColor ?? ""]?.main ??
            get(theme.palette, props.group[0].groupBackgroundColor) ??
            props.group[0].groupBackgroundColor
    }),
    MuiAccordionRoot: {
        "&:before": {
            display: "none"
        }
    }
}), { name: "DployFormSectionGroup" });
const SectionGroup = (props) => {
    const { group, enableTabs, className, elevation, square, transparent, disableExpansion, ...restProps } = props;
    const sectionGroupClasses = useSectionGroupStyles(props);
    const isFieldVisible = useTemplateFieldIsVisible();
    const [firstSection = null] = group;
    const [expanded, setExpanded] = useState(firstSection?.expandInitially ?? false);
    const { errors, touched, values } = useFormikContext();
    const hasErrors = firstSection?.fields.some(f => {
        const literal = firstSection?.literal || f.literal;
        if (f.errorDisplay === ErrorDisplay.Never ||
            (f.errorDisplayLiteral === ErrorDisplay.Never && literal))
            return false;
        const err = getIn(errors, f.namespace + "." + f.name);
        return err &&
            typeof err !== "boolean" &&
            getIn(touched, f.namespace + "." + f.name)
            ? err
            : undefined;
    }) ?? false;
    useEffect(() => {
        if (hasErrors)
            setExpanded(true);
    }, [hasErrors]);
    const getDebugSectionProps = useGetDebugSectionProps();
    const [sectionCompleted, setCompleted] = useState({});
    const setSectionCompleted = useCallback((id, completed) => setCompleted(completedSections => ({
        ...completedSections,
        [id]: completed
    })), []);
    const firstSectionVisibleFields = firstSection?.fields?.filter(isFieldVisible);
    const header = useSectionHeader(firstSection) ??
        firstSectionVisibleFields
            ?.map(x => x.label)
            .filter(x => x)
            .join("/");
    if (!firstSection)
        return null;
    const modifiedGroup = group.map(s => s.displaySectionTitle ? s : { ...s, sectionTitle: undefined });
    const panelClassName = clsx(className, sectionGroupClasses.root, {
        [sectionGroupClasses.transparent]: transparent,
        [sectionGroupClasses.hasTabs]: enableTabs,
        [sectionGroupClasses.groupBorder]: firstSection.groupBorder,
        [sectionGroupClasses.groupBackgroundColor]: firstSection.groupBackgroundColor
    });
    const gridComponentProps = {
        item: true,
        xs: 12,
        sm: firstSection.outerWidthSM || 12
    };
    if (!firstSection.group ||
        disableExpansion ||
        !firstSection.enableExpansion) {
        let restSections = group;
        let seactionAsSummary = undefined;
        if (firstSection.layout &&
            getRegisteredSectionLayouts()[firstSection.layout].settings
                .forceRenderAsHeaderWithoutGroup) {
            restSections = group.slice(1);
            seactionAsSummary = (_jsx(MockAccordionSummary, { className: clsx(sectionGroupClasses.expansionSummary, sectionGroupClasses.sectionGroup), children: _jsx(Grid, { container: true, children: _jsx(InnerSection, { expanded: true, s: firstSection, ...getDebugSectionProps(firstSection, {
                            ...restProps,
                            lastSection: group.length === 1
                        }) }) }) }));
        }
        return (_jsx(Accordion, { className: panelClassName, expanded: firstSection.expandInitially || false, elevation: transparent ? 0 : elevation, square: square, classes: {
                root: Number(elevation) > 0 ? "" : sectionGroupClasses.MuiAccordionRoot
            }, component: Grid, ...gridComponentProps, children: _jsxs(_Fragment, { children: [seactionAsSummary, restSections.length > 0 ? (_jsx(AccordionDetails, { className: clsx(sectionGroupClasses.expansionContent, sectionGroupClasses.sectionGroupWrapper), children: _jsx(Grid, { container: true, children: restSections.map((s, i) => (_jsx(InnerSection, { expanded: true, s: s, ...getDebugSectionProps(s, {
                                    ...restProps,
                                    lastSection: i === restSections.length - 1
                                }) }, s.formTemplateSectionId ?? i.toString()))) }) })) : null] }) }));
    }
    const collapsedGroupSections = modifiedGroup.filter(g => g.showWhenCollapsed);
    const expandedGroupSections = modifiedGroup.filter(g => !g.showWhenCollapsed);
    return (_jsx(Suspense, { fallback: _jsx(Accordion, { elevation: transparent ? 0 : elevation, square: square, className: clsx(panelClassName, sectionGroupClasses.expansionWrapper), children: _jsx(AccordionSummary, { className: clsx(sectionGroupClasses.expansionSummary, sectionGroupClasses.sectionGroup), expandIcon: _jsx(CircularProgress, {}), children: _jsx(Typography, { className: clsx(sectionGroupClasses.expansionLabel, {
                        [sectionGroupClasses.expansionLabelUppercase]: firstSection.expansionTitleUppercase,
                        [sectionGroupClasses.expansionLabelBold]: firstSection.boldExpansionTitle,
                        [sectionGroupClasses.centerSectionTitle]: firstSection.centerSectionTitle,
                        [sectionGroupClasses.marginLeft]: !firstSection.moveFirstItemToTitle
                    }), children: firstSection?.displaySectionTitle ? header : undefined }) }) }), children: _jsxs(Accordion, { expanded: expanded, onChange: () => setExpanded(!expanded), elevation: transparent ? 0 : elevation, square: square, className: clsx(panelClassName, sectionGroupClasses.expansionWrapper), component: Grid, "data-cr-field": disableExpansion
                ? "Group"
                : expanded
                    ? "ExpandedGroup"
                    : "ClosedGroup", ...gridComponentProps, children: [_jsx(AccordionSummary, { className: clsx(sectionGroupClasses.expansionSummary, sectionGroupClasses.sectionGroup), classes: {
                        expandIcon: clsx({
                            [sectionGroupClasses.expandIcon]: firstSection.moveFirstItemToTitle,
                            [sectionGroupClasses.expandIconCollapsed]: firstSection.moveFirstItemToTitle && !expanded
                        })
                    }, expandIcon: _jsx(ExpandMoreIcon, { className: sectionGroupClasses.expandIconInner }), children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { container: true, item: true, alignItems: "center", wrap: "nowrap", children: [firstSection.showStatus && (_jsx(Grid, { item: true, className: sectionGroupClasses.statusIcon, children: _jsx(Box, { borderRadius: "100px", p: 0.5, textAlign: "center", bgcolor: sectionCompleted[firstSection.formTemplateSectionId]
                                                ? "green"
                                                : "darkgrey" }) })), firstSection.groupIcon && (_jsx(Grid, { item: true, className: sectionGroupClasses.groupIconContainer, children: _jsx(Icon, { fontSize: "small", className: `fa fa-${firstSection.groupIcon}`, classes: { root: sectionGroupClasses.groupIcon } }) })), _jsx(Grid, { item: true, xs: 12, children: firstSection?.displaySectionTitle &&
                                            (firstSection.moveFirstItemToTitle && !expanded ? (_jsx(FieldContainer, { fields: [
                                                    {
                                                        ...firstSectionVisibleFields[0]
                                                    }
                                                ], literal: true })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { className: clsx(sectionGroupClasses.expansionLabel, {
                                                            [sectionGroupClasses.expansionLabelUppercase]: firstSection.expansionTitleUppercase,
                                                            [sectionGroupClasses.expansionLabelBold]: firstSection.boldExpansionTitle,
                                                            [sectionGroupClasses.centerSectionTitle]: firstSection.centerSectionTitle,
                                                            [sectionGroupClasses.marginLeft]: !firstSection.moveFirstItemToTitle,
                                                            [sectionGroupClasses.expansionLabelWithSubTitle]: firstSection.showShowAndHideMessages
                                                        }), children: _jsx("div", { children: header }) }), firstSection.showShowAndHideMessages && (_jsx(Typography, { className: clsx(sectionGroupClasses.expansionSubTitle, {
                                                            [sectionGroupClasses.centerSectionTitle]: firstSection.centerSectionTitle,
                                                            [sectionGroupClasses.marginLeft]: !firstSection.moveFirstItemToTitle
                                                        }), children: expanded ? (_jsx(FormattedMessage, { id: "template-form.section.expansion.hide", defaultMessage: "Hide details" })) : (_jsx(FormattedMessage, { id: "template-form.section.expansion.show", defaultMessage: "Show details" })) }))] }))) })] }), collapsedGroupSections.length > 0 && !expanded && (_jsx(Grid, { container: true, item: true, xs: 12, children: collapsedGroupSections.map((s, i) => (_jsx(InnerSection, { setSectionCompleted: setSectionCompleted, expanded: expanded, s: s, collapsedSection: true, ...getDebugSectionProps(s, {
                                        ...restProps,
                                        lastSection: i === collapsedGroupSections.length - 1
                                    }) }, s.formTemplateSectionId || i.toString()))) }))] }) }), _jsx(AccordionDetails, { className: clsx(sectionGroupClasses.expandedGroupContent, sectionGroupClasses.expansionContent, sectionGroupClasses.sectionGroupWrapper), children: _jsx(Grid, { container: true, children: expandedGroupSections.map((s, i) => (_jsx(InnerSection, { setSectionCompleted: setSectionCompleted, expanded: expanded, s: s, ...getDebugSectionProps(s, {
                                ...restProps,
                                lastSection: i === expandedGroupSections.length - 1
                            }) }, s.formTemplateSectionId || i.toString()))) }) })] }) }, firstSection.formTemplateSectionId));
};
const useInnerSectionStyles = makeStyles(theme => ({
    innerSection: {
        width: "100%",
        maxWidth: ({ fullWidth, maxWidth, s }) => s.fullWidth || (s.fullWidth === undefined && fullWidth)
            ? "none"
            : maxWidth
                ? maxWidth
                : 550,
        margin: ({ fullWidth, lastSection = false, s }) => s.fullWidth || (s.fullWidth === undefined && fullWidth)
            ? 0
            : lastSection
                ? "auto"
                : `auto auto ${theme.spacing(s.marginBottom ?? 4)}px auto`, //s.marginBottom can be set to 0,
        backgroundColor: ({ s }) => s.backgroundColor
    },
    section: {}
}), { name: "DployFormPanelInnerSection" });
const InnerSection = (props) => {
    const { s, classes, literal, justifyContent, className, setSectionCompleted, collapsedSection, expanded, wrap, desktopViewForMobile, ...sectionProps } = props;
    const { fields, layout, tableColumns, moveFirstItemToTitle } = s;
    const innerSectionClasses = useInnerSectionStyles(props);
    const isFieldVisible = useTemplateFieldIsVisible();
    const SectionComponent = (layout && customSections[layout]) || Section;
    const InnerSectionComponent = layout && customSections.InnerSectionComponents[layout];
    const { formIsLiteral, formIsDisabled } = useFormState();
    const visibleFields = fields.filter(isFieldVisible);
    const filteredFields = moveFirstItemToTitle
        ? visibleFields.slice(1, s.fields.length)
        : visibleFields;
    return (_jsx(Suspense, { fallback: _jsx(LinearProgress, {}), children: _jsx(Grid, { item: true, xs: 12, sm: s.innerWidthSM || 12, children: _jsx(SectionComponent, { className: clsx(innerSectionClasses.innerSection, className, {
                    backgroundColor: s.backgroundColor
                }), classes: classes, ...sectionProps, ...s, fields: filteredFields, tableColumns: tableColumns, allFields: fields, literal: s.literal || literal || formIsLiteral, disabled: formIsDisabled, collapsedSection: collapsedSection, expanded: expanded, justifyContent: justifyContent, wrap: wrap, setSectionCompleted: setSectionCompleted, desktopViewForMobile: desktopViewForMobile, InnerSectionComponent: InnerSectionComponent }) }) }));
};
PanelImpl.displayName = "DployFormPanel";
export default memo(PanelImpl);
const useMockAccordionSummaryStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        minHeight: 8 * 6,
        padding: theme.spacing(0, 2)
    },
    content: {
        display: "flex",
        flexGrow: 1,
        margin: "12px 0"
    }
}), {
    name: "DployMockAccordionSummary"
});
/**
 * Same formatting as AccordionSummary without the button semantics
 * @param props
 * @returns
 */
function MockAccordionSummary(props) {
    const classes = useMockAccordionSummaryStyles();
    const { children, className } = props;
    return (_jsx("div", { className: clsx(classes.root, className), children: _jsx("div", { className: classes.content, children: children }) }));
}
//# sourceMappingURL=Panel.js.map